'use client';

import { OtnWebSocketOptions } from '@/helpers/sockets';
import { Urls } from '@urls';
import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { OtnSocketMessage, OtnSocketMessageData } from '../helpers/sockets';

type ChannelCallback = (data: OtnSocketMessageData) => void;

interface WebSocketContextType {
	readyState: ReadyState;
	lastMessage: MessageEvent<any> | null;
	sendMessage: (message: string) => void;
	subscribeToChannel: (channel: string, gameId: string, callback: ChannelCallback) => () => void;
	joinChannel: (channel: string, gameId: string) => void;
	leaveChannel: (channel: string, gameId: string) => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export const useWebSocketContext = () => {
	const context = useContext(WebSocketContext);
	if (!context) {
		throw new Error('useWebSocketContext must be used within WebSocketProvider');
	}
	return context;
};

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const subscribersRef = useRef<Map<string, Set<ChannelCallback>>>(new Map());
	const joinedChannelsRef = useRef<Set<string>>(new Set());
	const lastFocusTimeRef = useRef<number>(Date.now());

	const { sendMessage, lastMessage, readyState, getWebSocket } = useWebSocket(
		Urls.NotificationsServer,
		OtnWebSocketOptions,
	);

	// Batch message processing to reduce re-renders
	const pendingMessages = useRef<Array<{ channel: string; data: OtnSocketMessageData }>>([]);
	const processingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isProcessing = useRef(false);

	const processPendingMessages = useCallback(() => {
		// Prevent concurrent processing
		if (isProcessing.current || pendingMessages.current.length === 0) {
			return;
		}

		isProcessing.current = true;
		const messages = [...pendingMessages.current];
		pendingMessages.current = [];

		// Use requestIdleCallback to process messages without blocking rendering
		const processNextBatch = () => {
			// Process up to 5 messages at a time (not a minimum requirement)
			// This prevents blocking when many messages arrive simultaneously
			// Single messages are still processed immediately after the 100ms timeout
			const batchSize = 5;
			const batch = messages.splice(0, batchSize);

			batch.forEach(({ channel, data }) => {
				const callbacks = subscribersRef.current.get(channel);
				if (callbacks) {
					callbacks.forEach((callback) => callback(data));
				}
			});

			if (messages.length > 0) {
				// Process remaining messages in next idle callback
				if ('requestIdleCallback' in window) {
					requestIdleCallback(processNextBatch, { timeout: 100 });
				} else {
					setTimeout(processNextBatch, 0);
				}
			} else {
				isProcessing.current = false;
			}
		};

		// Start processing
		if ('requestIdleCallback' in window) {
			requestIdleCallback(processNextBatch, { timeout: 100 });
		} else {
			setTimeout(processNextBatch, 0);
		}
	}, []);

	// Handle incoming messages with batching
	useEffect(() => {
		if (lastMessage?.data) {
			try {
				const socketMessage = JSON.parse(lastMessage.data) as OtnSocketMessage;
				const socketData = JSON.parse(socketMessage.data) as OtnSocketMessageData;
				const channel = socketMessage.channel;

				// Add to pending messages
				pendingMessages.current.push({ channel, data: socketData });

				// Clear existing timeout
				if (processingTimeout.current) {
					clearTimeout(processingTimeout.current);
				}

				// Process messages after a short delay to batch them
				// This timeout triggers regardless of message count - even a single message
				// will be processed after 100ms. The batching helps when multiple messages
				// arrive within this window.
				processingTimeout.current = setTimeout(() => {
					processPendingMessages();
				}, 100);
			} catch (error) {
				console.error('Error parsing WebSocket message:', error);
			}
		}
	}, [lastMessage, processPendingMessages]);

	const joinChannel = useCallback(
		(channel: string, gameId: string) => {
			const fullChannel = `public::${channel}::${gameId}`;

			if (readyState === ReadyState.OPEN && !joinedChannelsRef.current.has(fullChannel)) {
				sendMessage(
					JSON.stringify({
						data: {
							action: 'join_channel',
							channel: fullChannel,
							is_user_channel: false,
						},
					}),
				);
				joinedChannelsRef.current.add(fullChannel);
				console.log(`[WebSocket] Joined channel: ${fullChannel}`);
			}
		},
		[readyState, sendMessage],
	);

	const leaveChannel = useCallback(
		(channel: string, gameId: string) => {
			const fullChannel = `public::${channel}::${gameId}`;

			if (readyState === ReadyState.OPEN && joinedChannelsRef.current.has(fullChannel)) {
				sendMessage(
					JSON.stringify({
						data: {
							action: 'leave_channel',
							channel: fullChannel,
							is_user_channel: false,
						},
					}),
				);
				joinedChannelsRef.current.delete(fullChannel);
				console.log(`[WebSocket] Left channel: ${fullChannel}`);
			}
		},
		[readyState, sendMessage],
	);

	const subscribeToChannel = useCallback(
		(channel: string, gameId: string, callback: ChannelCallback) => {
			const fullChannel = `public::${channel}::${gameId}`;

			if (!subscribersRef.current.has(fullChannel)) {
				subscribersRef.current.set(fullChannel, new Set());
			}
			subscribersRef.current.get(fullChannel)!.add(callback);

			joinChannel(channel, gameId);

			return () => {
				const callbacks = subscribersRef.current.get(fullChannel);
				if (callbacks) {
					callbacks.delete(callback);

					// If no more subscribers, leave the channel
					if (callbacks.size === 0) {
						subscribersRef.current.delete(fullChannel);
						leaveChannel(channel, gameId);
					}
				}
			};
		},
		[joinChannel, leaveChannel],
	);

	// Re-join channels when connection opens
	useEffect(() => {
		if (readyState === ReadyState.OPEN) {
			// Re-join all channels that have subscribers
			subscribersRef.current.forEach((_, fullChannel) => {
				if (!joinedChannelsRef.current.has(fullChannel)) {
					const [, channel, gameId] = fullChannel.split('::');
					joinChannel(channel, gameId);
				}
			});
		}
	}, [readyState, joinChannel]);

	// Handle window focus/visibility changes
	useEffect(() => {
		const handleFocus = () => {
			const now = Date.now();
			const timeSinceLastFocus = now - lastFocusTimeRef.current;

			// If window was unfocused for more than 30 seconds, force reconnection
			if (timeSinceLastFocus > 30000) {
				console.log('[WebSocket] Window refocused after extended period, checking connection...');

				// Get the current WebSocket instance
				const ws = getWebSocket();

				// If the connection is closed or closing, the useWebSocket hook will handle reconnection
				// We just need to clear our joined channels so they get re-joined
				if (readyState !== ReadyState.OPEN) {
					console.log('[WebSocket] Connection not open, clearing joined channels for re-join on reconnect');
					joinedChannelsRef.current.clear();
				} else {
					// Connection is open but might be stale, send a ping to check
					try {
						sendMessage('ping');
					} catch (error) {
						console.error('[WebSocket] Failed to send ping, connection may be stale', error);
						// Clear joined channels so they get re-joined
						joinedChannelsRef.current.clear();
					}
				}
			}

			lastFocusTimeRef.current = now;
		};

		const handleBlur = () => {
			lastFocusTimeRef.current = Date.now();
		};

		// Listen for both focus and visibility change events
		window.addEventListener('focus', handleFocus);
		window.addEventListener('blur', handleBlur);
		document.addEventListener('visibilitychange', () => {
			if (!document.hidden) {
				handleFocus();
			} else {
				handleBlur();
			}
		});

		return () => {
			window.removeEventListener('focus', handleFocus);
			window.removeEventListener('blur', handleBlur);
			document.removeEventListener('visibilitychange', handleFocus);
		};
	}, [readyState, sendMessage, getWebSocket]);

	const value: WebSocketContextType = {
		readyState,
		lastMessage,
		sendMessage,
		subscribeToChannel,
		joinChannel,
		leaveChannel,
	};

	return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};
